import { useState } from 'react';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { TestBooking } from '../../lib/types';
import { Detail, ErrorNote, Field, PageHeader, RoleNotice, StatusBadge } from '../../ui/components';

const EXAMINER_ROLES = ['EXAMINER'];

/**
 * M21 — theory/practical driving test result recording.
 *
 * BACKEND GAP: m21-transport exposes no endpoint to list or search test
 * bookings for an examiner (only GET /road-test/me, which is the citizen's
 * own self-service view keyed off their own NIN claim, and the two
 * POST .../result endpoints which take a booking id directly — see
 * packages/modules/m21-transport/src/routes/licence.ts). Until a
 * GET /tests/{theory,practical}/:id or a search-by-nin endpoint exists for
 * officers, this screen can only record a result against a booking id the
 * examiner already has in hand (e.g. from the citizen's booking
 * confirmation or the C10 ServiceDesk appointment queue) — it cannot show
 * booking details before submission.
 */
export function Tests() {
  const { persona } = useAuth();
  const isExaminer = persona ? persona.roles.some((r) => EXAMINER_ROLES.includes(r)) : false;
  const [kind, setKind] = useState<'theory' | 'practical'>('theory');
  const [bookingId, setBookingId] = useState('');
  const [result, setResult] = useState<'PASS' | 'FAIL'>('PASS');
  const [examinerId, setExaminerId] = useState('');
  const [failReasons, setFailReasons] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recorded, setRecorded] = useState<TestBooking | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    setRecorded(null);
    try {
      const booking = await api.post<TestBooking>(`/api/v1/transport/tests/${kind}/${bookingId.trim()}/result`, {
        result,
        ...(examinerId.trim() ? { examinerId: examinerId.trim() } : {}),
        failReasons: result === 'FAIL' ? failReasons.split(',').map((s) => s.trim()).filter(Boolean) : [],
      });
      setRecorded(booking);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to record result');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader title="Driving Tests" subtitle="Record theory/practical test results against a booking id (M21 — LTA)." />

      {!isExaminer ? <RoleNotice roles={EXAMINER_ROLES} /> : null}

      <div className="card max-w-xl p-4">
        <p className="mb-4 text-xs text-slate-500">
          No booking-search endpoint exists yet for examiners — enter the booking id from the citizen's confirmation or the
          ServiceDesk appointment queue.
        </p>
        {error ? (
          <div className="mb-3">
            <ErrorNote message={error} />
          </div>
        ) : null}
        <div className="space-y-3">
          <Field label="Test type">
            <select className="field-input" value={kind} onChange={(e) => setKind(e.target.value as 'theory' | 'practical')}>
              <option value="theory">Theory</option>
              <option value="practical">Practical</option>
            </select>
          </Field>
          <Field label="Booking id">
            <input className="field-input" value={bookingId} onChange={(e) => setBookingId(e.target.value)} />
          </Field>
          <Field label="Result">
            <select className="field-input" value={result} onChange={(e) => setResult(e.target.value as 'PASS' | 'FAIL')}>
              <option value="PASS">PASS</option>
              <option value="FAIL">FAIL</option>
            </select>
          </Field>
          <Field label="Examiner id (optional — defaults to signed-in officer)">
            <input className="field-input" value={examinerId} onChange={(e) => setExaminerId(e.target.value)} />
          </Field>
          {result === 'FAIL' ? (
            <Field label="Fail reasons (comma-separated)">
              <input className="field-input" value={failReasons} onChange={(e) => setFailReasons(e.target.value)} />
            </Field>
          ) : null}
          <button
            className={result === 'PASS' ? 'btn-success' : 'btn-danger'}
            disabled={!isExaminer || busy || bookingId.trim().length === 0}
            onClick={submit}
          >
            {busy ? 'Recording…' : `Record ${result}`}
          </button>
        </div>
      </div>

      {recorded ? (
        <div className="card mt-6 max-w-xl p-4">
          <div className="mb-3 flex items-center justify-between">
            <div className="text-sm font-bold text-slate-900">Result recorded</div>
            <StatusBadge status={recorded.result ?? recorded.status} />
          </div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Detail label="NIN" value={recorded.nin} mono />
            <Detail label="Class" value={recorded.licenceClass} />
            <Detail label="Slot" value={formatDateTime(recorded.slot)} />
            <Detail label="Examiner" value={recorded.examinerId ?? '—'} />
          </div>
          {recorded.failReasons.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {recorded.failReasons.map((r) => (
                <span key={r} className="rounded bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-700">
                  {r}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
